<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-6 lg:flex-row">
      <DynamicStyle :color="page.template?.header_color_2 || '#7270C2'" />
      <div class="space-y-6 max-lg:mt-4 lg:basis-3/5">
        <BaseCard
          round
          class="card-base"
        >
          <h1 class="text-dynamic">
            {{ page.title }}
          </h1>
          <SofieText :value="page.template?.text" />
        </BaseCard>
        <div
          v-if="showResources"
          class="grid gap-2 md:grid-cols-2"
        >
          <BaseCard
            v-for="resource in resources"
            :key="resource.id"
            round
            class="card-base !p-6 font-medium hover:bg-violet-lighter"
          >
            <a
              v-if="resource.document?.[0]"
              :href="resource.document[0].url"
              download
              target="_blank"
              class="flex items-center gap-2"
            >
              <div class="grow">{{ resource.title }}</div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
            </a>
            <NuxtLink
              v-else
              :to="resource.url"
              target="_blank"
              class="flex items-center gap-2"
            >
              <div class="grow">
                {{ resource.title }}
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                />
              </svg>
            </NuxtLink>
          </BaseCard>
        </div>
      </div>

      <aside
        class="space-y-6 lg:basis-2/5"
        :class="{'light': !page.template?.sidebar_dark}"
      >
        <BaseCard
          v-if="page.active_image"
          round
          class="overflow-hidden !p-0"
        >
          <NuxtPicture
            :src="page.active_image.url"
            :alt="page.active_image.title"
          />
        </BaseCard>
        <FindWork v-if="page.template?.search" />
        <BaseCard
          v-if="page.template?.youtube_id"
          round
          class="overflow-hidden !p-0"
        >
          <iframe
            :src="`https://www.youtube-nocookie.com/embed/${page.template?.youtube_id}`"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
            allowfullscreen
            class="aspect-[16/9] w-full"
          />
        </BaseCard>
        <BaseCard
          v-if="page.template.reference"
          round
          class="space-y-6 bg-white text-center"
        >
          <div class="prose-lg">
            {{ page.template.reference.text }}
          </div>
          <div class="text-sm">
            {{ page.template.reference.title }}, <span class="text-gray-lighter">{{ page.template.reference.profession.title }}</span>
          </div>
        </BaseCard>
        <BaseCard
          v-if="page.template.text_sidebar"
          round
          class="prose sticky top-6"
          :style="`background-color: ${color}`"
        >
          <div>
            <SofieText
              :value="page.template.text_sidebar"
              :class="page.template?.sidebar_dark ? 'prose-invert' : ''"
            />
          </div>
        </BaseCard>
      </aside>
    </div>

    <BaseCard
      v-if="page.template.textWideBefore"
      round
      class="bg-white p-4 lg:p-10"
    >
      <SofieText :value="page.template.textWideBefore" />
    </BaseCard>
    <div
      v-if="page.template.show_branches"
      class="container-extend pb-10 pt-16 lg:pb-24 lg:pt-20"
    >
      <VacancyFittingSection />
    </div>
    <BaseCarousel
      v-if="page.images?.length"
      :items="page.images.filter(image => image.id !== page.active_image?.id)"
      type="default"
      class="container-extend"
    >
      <template #item="{url, title}">
        <div>
          <NuxtPicture
            :src="url"
            :img-attrs="{class: 'mask mask-rectangle block'}"
            :alt="title"
          />
        </div>
      </template>
    </BaseCarousel>

    <BaseCard
      v-if="page.template.textWide"
      round
      class="bg-white p-4 lg:p-10"
    >
      <SofieText :value="page.template.textWide" />
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import hulpmiddelenQuery from '~/graphql/queries/hulpmiddelen.gql';
import type {SubjectFilter} from '~/types';

const page = useSofiePage();

const color = computed(() => {
  return page.value.template?.sidebar_dark ? page.value.template?.header_color_1 || '#7270C2' : '#f1f0f9';
});

const textcolor = computed(() => {
  return page.value.template?.sidebar_dark ? '#f1f0f9' : page.value.template?.header_color_1 || '#7270C2';
});

const {result: hulpmiddelenResult} = useQuery(hulpmiddelenQuery);
const resources = computed<SubjectFilter>(() => hulpmiddelenResult.value?.hulpmiddelen ?? []);

const showResources = computed<boolean>(() => (useRoute().path.includes('werkgevers/formulieren')));
</script>

<style scoped>
aside {
  --dynamic: v-bind(textcolor);

  ::v-deep(form) {
    --dynamic: v-bind(color);
  }

  /* This is should be handled in form.ts */
  .prose > div ::v-deep(h2:first-child) {
    @apply mt-0;
  }

  /* This is a pretty dirty fix to support dark & light background forms with floating labels */
  &.light {
    ::v-deep(.text-white) {
      color: #333 !important;
    }

    ::v-deep(.border-gray-lightest) {
      border-color: #333 !important;
    }
  }
}
</style>
